export type HowToStep = { title: string; desc: string }
export type FaqItem = { question: string; answer: string }
export type WhereItem = { title: string; variant: string }
export type WhyItem = { title: string; desc: string; image: string }
export type FeatureItem = { title: string; desc: string; image: string }
export type QuoteItem = { title: string; text: string; name: string; job: string; avatar: string }
export type WhyNeedItem = { title: string; desc: string }
export type ApiPoint = { title: string; desc: string }

export type LandingSeo = {
  h1: string
  howto: HowToStep[]
  whereTitle: string
  whereItems: WhereItem[]
  apiTitle: string
  apiPoints: ApiPoint[]
  apiLinkText: string
  apiHref: string
  featuresTitle: string
  featuresItems: FeatureItem[]
  whyNeedTitle: string
  whyNeedItems: WhyNeedItem[]
  whyTitle: string
  whyItems: WhyItem[]
  quoteTitle: string
  quoteItems: QuoteItem[]
  faqTitle: string
  faq: FaqItem[]
}

export type LandingH2SectionType = 'howTo' | 'useCases' | 'api' | 'features' | 'benefits' | 'whyChoose' | 'testimonials' | 'faq'
export type LandingH2Bindings = Record<string, LandingH2SectionType>

function splitByH2(source: string) {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const h1 = lines.find((l) => l.startsWith('# '))?.slice(2).trim() || ''

  const sections: { h2: string; body: string }[] = []
  let current: { h2: string; bodyLines: string[] } | null = null

  for (const line of lines) {
    if (line.startsWith('## ')) {
      if (current) sections.push({ h2: current.h2, body: current.bodyLines.join('\n').trim() })
      current = { h2: line.slice(3).trim(), bodyLines: [] }
      continue
    }
    if (line.startsWith('# ')) continue
    if (!current) continue
    current.bodyLines.push(line)
  }

  if (current) sections.push({ h2: current.h2, body: current.bodyLines.join('\n').trim() })
  return { h1, sections }
}

function parseHowTo(body: string): HowToStep[] {
  const lines = body.split('\n')
  const steps: HowToStep[] = []
  let currentTitle = ''
  let currentDesc: string[] = []

  for (const line of lines) {
    const t = line.trim()
    if (t.startsWith('### ')) {
      if (currentTitle) steps.push({ title: currentTitle, desc: currentDesc.join(' ').trim() })
      currentTitle = t.slice(4).replace(/^Step\d+：/, '').trim()
      currentDesc = []
      continue
    }
    if (!t) continue
    if (!currentTitle) continue
    if (t === '<br />') continue
    currentDesc.push(t)
  }

  if (currentTitle) steps.push({ title: currentTitle, desc: currentDesc.join(' ').trim() })
  return steps.slice(0, 3)
}

function parseBullets(body: string) {
  return body
    .split('\n')
    .map((x) => x.trim())
    .filter((x) => x.startsWith('- '))
    .map((x) => x.slice(2).trim())
}

function parseH3Titles(body: string) {
  return body
    .split('\n')
    .map((x) => x.trim())
    .filter((x) => x.startsWith('### '))
    .map((x) => x.slice(4).trim())
}

function guessVariant(title: string) {
  const t = title.toLowerCase()
  if (t.includes('ai') && t.includes('水印')) return 'ai'
  if (t.includes('豆包')) return 'doubao'
  if (t.includes('即梦')) return 'jimeng'
  if (t.includes('小红书')) return 'xiaohongshu'
  if (t.includes('b站') || t.includes('bilibili')) return 'bilibili'
  if (t.includes('抖音') || t.includes('douyin')) return 'douyin'
  if (t.includes('tk') || t.includes('tiktok')) return 'tiktok'
  if (t.includes('文字')) return 'text'
  if (t.includes('logo')) return 'logo'
  return 'text'
}

function parseWhy(body: string): Array<{ title: string; desc: string }> {
  const lines = body.split('\n')
  const items: Array<{ title: string; desc: string }> = []
  let currentTitle = ''
  let currentDesc: string[] = []

  for (const line of lines) {
    const t = line.trim()
    if (t.startsWith('### ')) {
      if (currentTitle) items.push({ title: currentTitle, desc: currentDesc.join(' ').trim() })
      currentTitle = t.slice(4).replace(/^\d+\.\s*/, '').trim()
      currentDesc = []
      continue
    }
    if (!t) continue
    if (!currentTitle) continue
    if (t === '<br />') continue
    currentDesc.push(t)
  }

  if (currentTitle) items.push({ title: currentTitle, desc: currentDesc.join(' ').trim() })
  return items.slice(0, 4)
}

function parseH3Sections(body: string): Array<{ title: string; desc: string }> {
  const lines = body.split('\n')
  const items: Array<{ title: string; desc: string }> = []
  let currentTitle = ''
  let currentDesc: string[] = []

  for (const line of lines) {
    const t = line.trim()
    if (t.startsWith('### ')) {
      if (currentTitle) items.push({ title: currentTitle, desc: currentDesc.join(' ').trim() })
      currentTitle = t.slice(4).trim()
      currentDesc = []
      continue
    }
    if (!t) continue
    if (!currentTitle) continue
    if (t === '<br />') continue
    currentDesc.push(t)
  }

  if (currentTitle) items.push({ title: currentTitle, desc: currentDesc.join(' ').trim() })
  return items
}

function parseQuoteBlocks(body: string) {
  const lines = body
    .split('\n')
    .map((x) => x.trim())
    .filter((x) => x && x !== '<br />')

  const blocks: Array<{ title: string; lines: string[] }> = []
  let currentTitle = ''
  let currentLines: string[] = []

  const flush = () => {
    if (!currentTitle) return
    blocks.push({ title: currentTitle, lines: currentLines.slice() })
    currentTitle = ''
    currentLines = []
  }

  for (const l of lines) {
    if (l.startsWith('### ')) {
      flush()
      currentTitle = l.slice(4).trim()
      continue
    }
    const m = l.match(/^\*\*(.+?)\*\*$/)
    if (m?.[1]) {
      flush()
      currentTitle = m[1].trim()
      continue
    }
    if (!currentTitle) continue
    currentLines.push(l)
  }
  flush()
  return blocks
}

function parseQuotes(body: string): QuoteItem[] {
  const blocks = parseQuoteBlocks(body)
  const out: QuoteItem[] = []
  for (const b of blocks) {
    const textLines = b.lines.map((x) => String(x || '').trim()).filter(Boolean)
    const nameLine = textLines.find((l) => /^-\s*姓名\s*[:：]/.test(l))
    const jobLine = textLines.find((l) => /^-\s*职位\s*[:：]/.test(l))
    const name = nameLine ? nameLine.replace(/^-\s*姓名\s*[:：]\s*/, '').trim() : ''
    const job = jobLine ? jobLine.replace(/^-\s*职位\s*[:：]\s*/, '').trim() : ''
    const contentLines = textLines.filter((l) => !/^-\s*(姓名|职位)\s*[:：]/.test(l))

    if (name || job) {
      const mainText = contentLines.join(' ').trim() || ''
      out.push({ title: b.title, text: mainText, name, job, avatar: '' })
      continue
    }

    const meta = contentLines.length ? (contentLines[contentLines.length - 1] || '') : ''
    const metaParts = meta.split('/').map((x: string) => x.trim()).filter(Boolean)
    const legacyName = metaParts[0] || ''
    const legacyJob = metaParts[1] || ''
    const mainText = (metaParts.length >= 2 ? contentLines.slice(0, -1) : contentLines).join(' ').trim() || ''
    out.push({ title: b.title, text: mainText, name: legacyName, job: legacyJob, avatar: '' })
  }
  return out
}

function parseFaq(body: string): FaqItem[] {
  const lines = body
    .split('\n')
    .map((x) => x.trim())
    .filter((x) => x && x !== '<br />')

  const out: FaqItem[] = []
  let currentQ = ''
  let currentA: string[] = []

  const flush = () => {
    if (!currentQ) return
    out.push({ question: currentQ, answer: currentA.join(' ').trim() })
    currentQ = ''
    currentA = []
  }

  for (const l of lines) {
    if (l.startsWith('### ')) {
      flush()
      currentQ = l.slice(4).trim()
      continue
    }
    if (!currentQ) continue
    currentA.push(l.replace(/&#x20;|&nbsp;/g, ' ').trim())
  }
  flush()
  return out
}

function firstMatchKey(map: Map<string, string>, patterns: RegExp[]) {
  for (const [k] of map) {
    for (const p of patterns) {
      if (p.test(k)) return k
    }
  }
  return ''
}

function buildFallbackWhereItems(h1: string) {
  const t = String(h1 || '')
  const platform =
    /b站|bilibili/i.test(t)
      ? 'B站'
      : /抖音|douyin/i.test(t)
        ? '抖音'
        : /tiktok|\\btk\\b/i.test(t)
          ? 'TikTok'
          : /小红书/i.test(t)
            ? '小红书'
            : ''
  const base = platform || '视频'
  const titles = [
    `${base}水印`,
    `${base}字幕`,
    `${base}台标`,
    `${base}logo`,
    `${base}角标`,
    `移动水印`,
    `半透明水印`,
    `贴纸水印`,
    `文字水印`,
    `图片水印`,
    `背景水印`,
    `片头片尾水印`,
  ]
  return titles.map((x) => ({ title: x, variant: guessVariant(x) }))
}

export function parseLandingSeo(source: string, bindings: LandingH2Bindings): LandingSeo {
  const { h1, sections } = splitByH2(source)
  const sectionByTitle = new Map(sections.map((s) => [s.h2, s.body]))
  const out: LandingSeo = {
    h1,
    howto: [],
    whereTitle: '',
    whereItems: [],
    apiTitle: '',
    apiPoints: [],
    apiLinkText: '',
    apiHref: '',
    featuresTitle: '',
    featuresItems: [],
    whyNeedTitle: '',
    whyNeedItems: [],
    whyTitle: '',
    whyItems: [],
    quoteTitle: '',
    quoteItems: [],
    faqTitle: '',
    faq: [],
  }

  const missing: string[] = []
  for (const [h2, type] of Object.entries(bindings || {})) {
    const body = sectionByTitle.get(h2) || ''
    if (!body) {
      missing.push(h2)
      continue
    }
    if (type === 'howTo') {
      if (!out.howto.length) out.howto = parseHowTo(body)
      continue
    }
    if (type === 'useCases') {
      const titles = parseH3Titles(body)
      const raw = titles.length ? titles : parseBullets(body)
      if (!out.whereTitle) out.whereTitle = h2
      const next = raw.map((t) => ({ title: t, variant: guessVariant(t) }))
      const merged = [...out.whereItems, ...next]
      const seen = new Set<string>()
      out.whereItems = merged.filter((x) => {
        const k = String(x?.title || '').trim()
        if (!k) return false
        if (seen.has(k)) return false
        seen.add(k)
        return true
      })
      continue
    }
    if (type === 'api') {
      const link = body.match(/\[([^\]]+)\]\(([^)]+)\)/)
      const linkText = link?.[1] ? String(link[1]).trim() : ''
      const href = link?.[2] ? String(link[2]).trim() : ''
      const bodyWithoutLink = link?.[0] ? body.replace(link[0], '').trim() : body
      const points = parseH3Sections(bodyWithoutLink).slice(0, 6)
      if (!out.apiTitle) out.apiTitle = h2
      if (!out.apiLinkText && linkText) out.apiLinkText = linkText
      if (!out.apiHref && href) out.apiHref = href

      const merged = [...out.apiPoints, ...points.map((x) => ({ title: x.title, desc: x.desc }))]
      const seen = new Set<string>()
      out.apiPoints = merged.filter((x) => {
        const k = String(x?.title || '').trim()
        if (!k) return false
        if (seen.has(k)) return false
        seen.add(k)
        return true
      })
      continue
    }
    if (type === 'features') {
      const featureRaw = parseH3Sections(body).slice(0, 4)
      const featureImages = [
        '/assets/why-ai-retouch-3x2.jpg',
        '/assets/why-one-click-3x2.jpg',
        '/assets/why-hd-output-3x2.jpg',
        '/assets/why-batch-fast-3x2.jpg',
      ] as const
      if (!out.featuresTitle) out.featuresTitle = h2
      const start = out.featuresItems.length
      out.featuresItems = [
        ...out.featuresItems,
        ...featureRaw.map((x, idx) => ({
          title: x.title,
          desc: x.desc,
          image: featureImages[Math.min(start + idx, featureImages.length - 1)] || featureImages[0],
        })),
      ]
      continue
    }
    if (type === 'benefits') {
      if (!out.whyNeedTitle) out.whyNeedTitle = h2
      out.whyNeedItems = [...out.whyNeedItems, ...parseH3Sections(body).slice(0, 4)]
      continue
    }
    if (type === 'whyChoose') {
      const whyRaw = parseWhy(body)
      const whyImages = [
        '/assets/why-ai-retouch-3x2.jpg',
        '/assets/why-one-click-3x2.jpg',
        '/assets/why-hd-output-3x2.jpg',
        '/assets/why-batch-fast-3x2.jpg',
      ] as const
      if (!out.whyTitle) out.whyTitle = h2
      const start = out.whyItems.length
      out.whyItems = [
        ...out.whyItems,
        ...whyRaw.map((x, idx) => ({
          title: x.title,
          desc: x.desc,
          image: whyImages[Math.min(start + idx, whyImages.length - 1)] || whyImages[0],
        })),
      ]
      continue
    }
    if (type === 'testimonials') {
      if (!out.quoteTitle) out.quoteTitle = h2
      out.quoteItems = [...out.quoteItems, ...parseQuotes(body)]
      continue
    }
    if (type === 'faq') {
      if (!out.faqTitle) out.faqTitle = h2
      out.faq = [...out.faq, ...parseFaq(body)]
    }
  }

  if (missing.length) {
    const available = Array.from(sectionByTitle.keys())
    try {
      console.warn(`[landingSeo] H2 bindings not found: ${missing.map((x) => `"${x}"`).join(', ')}. Available H2: ${available.map((x) => `"${x}"`).join(', ')}`)
    } catch {}
  }

  return out
}

export function parseVideoWatermarkRemovalSeo(source: string): LandingSeo {
  const { h1, sections } = splitByH2(source)
  const map = new Map(sections.map((s) => [s.h2, s.body]))

  const howtoKey =
    map.has('如何消除视频上的水印')
      ? '如何消除视频上的水印'
      : firstMatchKey(map, [/^如何.*(去除|消除).*(视频)?.*(水印|字幕)/, /^如何.*\\d+步/])
  const howto = parseHowTo(map.get(howtoKey) || '')

  const whereTitle = map.has('无痕AI能消除哪些视频水印') ? '无痕AI能消除哪些视频水印' : '适用场景'
  const whereKey = map.has(whereTitle) ? whereTitle : firstMatchKey(map, [/适用场景/, /能消除.*水印/])
  const whereRaw = map.get(whereKey) || ''
  const whereTitles = parseH3Titles(whereRaw)
  const whereItems: WhereItem[] = (whereTitles.length ? whereTitles : parseBullets(whereRaw)).length
    ? (whereTitles.length ? whereTitles : parseBullets(whereRaw)).map((t) => ({ title: t, variant: guessVariant(t) }))
    : buildFallbackWhereItems(h1)

  const whyNeedKey =
    map.has('为什么需要去除视频水印')
      ? '为什么需要去除视频水印'
      : map.has('为什么需要去除水印')
        ? '为什么需要去除水印'
        : firstMatchKey(map, [/^为什么需要.*去除.*水印/, /^为什么需要.*去水印/])
  const whyNeedTitle = whyNeedKey || ''
  const whyNeedItems: WhyNeedItem[] = whyNeedTitle ? parseH3Sections(map.get(whyNeedTitle) || '').slice(0, 4) : []

  const whyTitle =
    map.has('为什么选择无痕AI视频去水印')
      ? '为什么选择无痕AI视频去水印'
      : map.has('为什么选择我们的 AI 视频去水印技术？')
        ? '为什么选择我们的 AI 视频去水印技术？'
        : firstMatchKey(map, [/^为什么选择/, /选择无痕AI/]) || '为什么选择我们的 AI 视频去水印技术？'
  const whyRaw = parseWhy(map.get(whyTitle) || '')
  const whyImages = [
    '/assets/why-ai-retouch-3x2.jpg',
    '/assets/why-one-click-3x2.jpg',
    '/assets/why-hd-output-3x2.jpg',
    '/assets/why-batch-fast-3x2.jpg'
  ] as const
  const whyItems: WhyItem[] = whyRaw.map((x, idx) => ({
    title: x.title,
    desc: x.desc,
    image: whyImages[Math.min(idx, whyImages.length - 1)] || whyImages[0],
  }))

  const quoteKey =
    map.has('用户口碑：听听他们如何评价效率的飞跃')
      ? '用户口碑：听听他们如何评价效率的飞跃'
      : firstMatchKey(map, [/用户口碑/, /推荐信/, /评价.*效率/, /效率.*飞跃/, /客户.*效率/])
  const quoteTitle = quoteKey || '用户口碑'
  const quoteItems: QuoteItem[] = parseQuotes(map.get(quoteKey) || '')

  const faqKey = map.has('视频去水印常见问题 (FAQ)') ? '视频去水印常见问题 (FAQ)' : firstMatchKey(map, [/FAQ/i, /常见问题/])
  const faqTitle = faqKey || '常见问题'
  const faq = parseFaq(map.get(faqKey) || '')

  return {
    h1,
    howto,
    whereTitle,
    whereItems,
    apiTitle: '',
    apiPoints: [],
    apiLinkText: '',
    apiHref: '',
    featuresTitle: '',
    featuresItems: [],
    whyNeedTitle,
    whyNeedItems,
    whyTitle,
    whyItems,
    quoteTitle,
    quoteItems,
    faqTitle,
    faq,
  }
}
